import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { AlertTriangle, Trash2, ShieldOff } from "lucide-react";
import { toast } from "sonner";

const RED = "#DC2626";

/**
 * "Delete my account" Danger Zone on /profile.
 *
 * Two-step confirmation:
 *   1. Click the red button to open a confirmation dialog.
 *   2. Type DELETE + enter the current password, then confirm.
 *
 * This satisfies the Privacy Policy §8 promise that users can self-delete.
 * The backend endpoint purges every collection tied to the user before
 * removing the user document itself.
 */
export default function DeleteAccountCard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = password.length >= 1 && confirmPhrase.trim().toUpperCase() === "DELETE";

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setPassword("");
    setConfirmPhrase("");
  };

  const confirmDelete = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.request({
        method: "DELETE",
        url: "/auth/account",
        data: { password, confirm_phrase: confirmPhrase },
      });
      const total = Object.values(data?.deleted_counts || {}).reduce((a, b) => a + Number(b || 0), 0);
      toast.success(`Account deleted · ${total} record${total === 1 ? "" : "s"} purged`);
      // Navigate to /signup FIRST — logout() clears the token and hard-redirects
      // to /login, so we need to be on the desired route before that fires.
      navigate("/signup", { replace: true });
      try { await logout(); } catch { /* ignore */ }
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card className="mt-8 border-2 border-red-200 bg-red-50/40 rounded-xl p-5" data-testid="delete-account-card">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center shrink-0" style={{ color: RED }}>
            <ShieldOff className="w-4.5 h-4.5" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: RED }}>
              Danger zone
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mt-0.5">Delete my account</h3>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Permanently erases your account and every associated record — shots, meals, weigh-ins,
              vials, cycles, messages, community posts, and coaching links. This action cannot be undone.
              Download your data first from the History and Nutrition pages if you'd like a copy.
            </p>
            <div className="mt-3">
              <Button
                type="button"
                onClick={() => setOpen(true)}
                className="rounded-none uppercase tracking-widest text-xs font-semibold h-10 px-4"
                style={{ background: RED, color: "#FFFFFF" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#B91C1C")}
                onMouseLeave={(e) => (e.currentTarget.style.background = RED)}
                data-testid="delete-account-open"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete my account
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent className="sm:max-w-md bg-white border-red-200 rounded-none" data-testid="delete-account-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-red-50" style={{ color: RED }}>
                <AlertTriangle className="w-4 h-4" strokeWidth={2.2} />
              </div>
              <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: RED }}>
                This cannot be undone
              </div>
            </div>
            <DialogTitle className="text-slate-900 text-xl">Delete your account?</DialogTitle>
            <DialogDescription className="text-slate-600 mt-1">
              We'll permanently delete <b>{user?.email}</b> and every log, message, and setting tied to it.
              Type <span className="font-mono font-semibold text-red-700">DELETE</span> and re-enter your
              password to confirm.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={confirmDelete} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-widest text-slate-500">Current password</Label>
              <Input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="h-11 rounded-none bg-white border-slate-200"
                data-testid="delete-account-password"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-widest text-slate-500">Type DELETE to confirm</Label>
              <Input
                type="text"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder="DELETE"
                className="h-11 rounded-none bg-white border-slate-200 font-mono"
                data-testid="delete-account-confirm-phrase"
              />
            </div>

            <DialogFooter className="pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={close}
                disabled={submitting}
                className="border-slate-200 bg-white hover:bg-slate-50 text-slate-800 rounded-none uppercase tracking-widest text-xs font-semibold"
                data-testid="delete-account-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || submitting}
                className="rounded-none uppercase tracking-widest text-xs font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: RED }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = "#B91C1C"; }}
                onMouseLeave={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = RED; }}
                data-testid="delete-account-confirm"
              >
                {submitting ? "Deleting…" : "Permanently delete"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
